/* ============================================================================
   Asset Request (ticket) — static frontend constants.

   ISSUE TYPE is a fixed, frontend-only list (no master / no API). The employee
   picks one when raising a ticket; an admin can reclassify it from the request
   detail screen. (Stored on the request as `request_type`.)

   Priority and Status are likewise enumerated here. Tailwind class strings are
   written out in full (not interpolated) so the JIT compiler keeps them.
   ========================================================================== */

// --- Issue Type (static, employee-selected; default = New Asset Request) -----
export const ASSET_REQUEST_TYPES = [
  "New Asset Request",
  "Incident / Problem",
  "Asset Replacement",
  "Asset Repair",
  "Software Installation",
  "Access Request",
];
export const DEFAULT_ASSET_REQUEST_TYPE = "New Asset Request";

// Short labels for chips/tabs.
const SHORT_TYPE = {
  "New Asset Request": "New Asset",
  "Incident / Problem": "Incident",
  "Asset Replacement": "Replacement",
  "Asset Repair": "Repair",
  "Software Installation": "Software",
  "Access Request": "Access",
};

// --- Priority ----------------------------------------------------------------
export const ASSET_REQUEST_PRIORITIES = ["Low", "Medium", "High", "Critical"];
export const DEFAULT_ASSET_REQUEST_PRIORITY = "Medium";

// --- Status workflow ---------------------------------------------------------
// Pending → Approved → Issued → Returned. Rejected (by admin) and Cancelled
// (withdrawn by the employee) are terminal off-ramps.
export const ASSET_REQUEST_STATUSES = [
  "Pending",
  "Approved",
  "Issued",
  "Rejected",
  "Cancelled",
  "Returned",
];

// --- Badge class maps (full literals for Tailwind) ---------------------------
const STATUS_CLASS = {
  Pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  Approved: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  Issued: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  Rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  Cancelled: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  Returned: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
};
const PRIORITY_CLASS = {
  Low: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  Medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  High: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  Critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};
const TYPE_CLASS = {
  "New Asset Request": "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  "Incident / Problem": "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  "Asset Replacement": "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  "Asset Repair": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  "Software Installation": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  "Access Request": "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
};
const FALLBACK = "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";

export const assetReqStatusClass = (s) => STATUS_CLASS[s] || FALLBACK;
export const assetReqPriorityClass = (p) => PRIORITY_CLASS[p] || FALLBACK;
export const assetReqTypeClass = (t) => TYPE_CLASS[t] || FALLBACK;

// Short label for issue-type chips/tabs.
export const shortType = (t) => SHORT_TYPE[t] || (t || "").replace(" Request", "");

/* ----------------------------------------------------------------------------
   Backend field contract (asset_management/asset_request/) — the screens read
   these accessors. Adjust here (and in the screens) if the API differs.

   list/detail item:
     id, request_no, requester_name, employee_id, department,
     category, category_name, sub_category, sub_category_name,
     request_type (= issue type), subject, priority, status, justification,
     decision_note, assigned_asset, assigned_asset_name, assigned_asset_tag,
     attachments: [{ url, name }], created_at, approved_at, issued_at,
     approval_log: [{ created_at, action, by }]

   employee create payload (multipart/form-data when attachments present):
     { company_id, category, priority, request_type, subject, justification,
       attachment_1, attachment_2, … }
   admin update payload:     { id, status, decision_note?, request_type?,
                               assigned_asset? }
---------------------------------------------------------------------------- */
